import { z } from 'zod';
import { parseCrmTabEntityId } from './crm-tab';

/**
 * Versioned legal documents for the Uspacy Calls Beta.
 *
 * These identifiers are part of the acceptance evidence, so changing copy in
 * a way that changes the bargain or the processing instruction requires a new
 * version here. The placement receives this registry from the server and must
 * echo the exact identifiers and versions back; a stale cached client is
 * rejected rather than silently accepting a document the user did not see.
 *
 * Docs ahead of code: processing instruction 1.1 names Uspacy chats "where the
 * organization connects" them, so the first confirmation already covers them
 * (owner decision 2026-09-29). The intake accepts calls only today; #1263
 * closes the gap and removes this note.
 */
export const USPACY_LEGAL_DOCUMENTS = {
  terms: {
    documentId: 'aelo-uspacy-terms',
    version: '1.1',
    effectiveDate: '2026-10-07',
    path: 'uspacy-terms',
    scope: 'user',
    action: 'accepted',
  },
  privacy: {
    documentId: 'aelo-uspacy-privacy',
    version: '1.21',
    effectiveDate: '2026-11-03',
    path: 'uspacy-privacy',
    scope: 'user',
    action: 'acknowledged',
  },
  processingInstruction: {
    documentId: 'aelo-uspacy-processing-instruction',
    version: '1.21',
    effectiveDate: '2026-11-03',
    path: 'uspacy-processing-instruction',
    scope: 'organization',
    action: 'confirmed',
  },
} as const;

export const uspacyPlacementLocaleSchema = z.enum(['uk', 'en', 'es', 'de']);

export type UspacyPlacementLocale = z.infer<typeof uspacyPlacementLocaleSchema>;

/**
 * What happens to a new call from the portal, as `GET /placement/status`
 * reports it (#2369). The intake and pipeline gates in the order they apply:
 *
 * - `not_processing` — new calls are not analysed for a reason the customer
 *   fixes in Aelo: intake drops them (no default project, the project is not
 *   active, the call channel is off) or the platform paused the organization
 *   out of funds. Deliberately one value without the reason: the status
 *   answers ANY portal user, and the organization's funds and project state
 *   are shown in Aelo, not to every employee of the portal (owner, 21.10).
 * - `manual` — the organization's Manual mode: the record waits for a start.
 * - `transcription_only` — transcribed on arrival, but the default project's
 *   autoAnalysis is off, so the analysis waits for a start.
 * - `automatic` — transcribed and analysed on arrival.
 *
 * The shell bundle deploys separately from edge-api and must treat a value it
 * does not know as "no mode", never as one of these.
 */
export const uspacyPlacementAnalysisModeSchema = z.enum([
  'automatic',
  'manual',
  'transcription_only',
  'not_processing',
]);

export type UspacyPlacementAnalysisMode = z.infer<
  typeof uspacyPlacementAnalysisModeSchema
>;

/** Normalize the locale values documented by Uspacy to the four listed locales. */
export function normalizeUspacyPlacementLocale(
  value: string | null | undefined
): UspacyPlacementLocale {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'uk' || normalized?.startsWith('uk-')) return 'uk';
  if (normalized === 'es' || normalized?.startsWith('es-')) return 'es';
  if (normalized === 'de' || normalized?.startsWith('de-')) return 'de';
  return 'en';
}

/**
 * One document's evidence: which document, which version, and the single
 * action that document takes ('accepted' / 'acknowledged' / 'confirmed').
 *
 * `action` is generic and the computed key is cast to a mapped type on
 * purpose. Written as a plain `[action]: z.literal(true)` on a non-generic
 * parameter, TypeScript widens the whole object to
 * `Record<string, true | '<id>' | '<version>'>` — the literal field names
 * vanish from the inferred type, so `activation.terms.accepted` type-checks
 * under ANY name and the extension's compile-time parity guard could never
 * fail. Runtime validation was always correct (`.strict()` accepts exactly the
 * one key); it was the TYPE that carried no information.
 */
const legalDocumentEvidenceSchema = <
  TId extends string,
  TVersion extends string,
  TAction extends 'accepted' | 'acknowledged' | 'confirmed',
>(
  documentId: TId,
  version: TVersion,
  action: TAction
) =>
  z
    .object({
      documentId: z.literal(documentId),
      version: z.literal(version),
      ...({ [action]: z.literal(true) } as { [K in TAction]: z.ZodLiteral<true> }),
    })
    .strict();

export const uspacyTermsAcceptanceSchema = legalDocumentEvidenceSchema(
  USPACY_LEGAL_DOCUMENTS.terms.documentId,
  USPACY_LEGAL_DOCUMENTS.terms.version,
  'accepted'
);

export const uspacyPrivacyAcknowledgementSchema = legalDocumentEvidenceSchema(
  USPACY_LEGAL_DOCUMENTS.privacy.documentId,
  USPACY_LEGAL_DOCUMENTS.privacy.version,
  'acknowledged'
);

export const uspacyProcessingInstructionConfirmationSchema = legalDocumentEvidenceSchema(
  USPACY_LEGAL_DOCUMENTS.processingInstruction.documentId,
  USPACY_LEGAL_DOCUMENTS.processingInstruction.version,
  'confirmed'
).extend({ authorityConfirmed: z.literal(true) });

/**
 * The three legally distinct actions collected before organization-wide call
 * processing starts. Terms are accepted, the privacy notice is acknowledged,
 * and an authorized representative gives a documented processing instruction.
 */
export const uspacyLegalActivationSchema = z
  .object({
    terms: uspacyTermsAcceptanceSchema,
    privacy: uspacyPrivacyAcknowledgementSchema,
    processingInstruction: uspacyProcessingInstructionConfirmationSchema,
    language: uspacyPlacementLocaleSchema,
  })
  .strict();

export const uspacyPlacementOnboardRequestSchema = z
  .object({ legal: uspacyLegalActivationSchema })
  .strict();

/**
 * Organization lifecycle command for a linked portal, issued from Aelo
 * settings by an authorized Customer representative.
 *
 * Pausing and withdrawing consume the instruction already on file; activating
 * IS a fresh instruction and therefore carries the full legal payload — which
 * is why this is a discriminated union rather than a bare state string.
 */
export const uspacyProcessingCommandSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('paused') }).strict(),
  z.object({ state: z.literal('withdrawn') }).strict(),
  z
    .object({
      state: z.literal('active'),
      legal: uspacyLegalActivationSchema,
    })
    .strict(),
]);

export const uspacyProcessingStateValueSchema = z.enum([
  'pending',
  'active',
  'paused',
  'withdrawn',
]);

export const uspacyProcessingSubscriptionStatusSchema = z.enum([
  'not_started',
  'pending',
  'active',
  'failed',
  'removed',
]);

/** Answer to a lifecycle command; the two booleans are mutually exclusive. */
export const uspacyProcessingCommandResultSchema = z.object({
  state: z.enum(['active', 'paused', 'withdrawn']),
  revision: z.number().int().nonnegative(),
  subscriptionRemoved: z.boolean().optional(),
  subscriptionActive: z.boolean().optional(),
});

export const uspacyLegalDocumentLinkSchema = z.object({
  documentId: z.string().min(1),
  version: z.string().min(1),
  effectiveDate: z.string().date(),
  url: z.string().url(),
  language: z.enum(['uk', 'en', 'es']),
  /** Locale requested by the host; the current legal body is governing EN. */
  requestedLanguage: uspacyPlacementLocaleSchema,
  governingLanguage: z.literal('en'),
  isLanguageFallback: z.boolean(),
});

export const uspacyLegalDocumentsSchema = z.object({
  terms: uspacyLegalDocumentLinkSchema,
  privacy: uspacyLegalDocumentLinkSchema,
  processingInstruction: uspacyLegalDocumentLinkSchema,
});

/** What the settings surface may render about the current instruction. */
export const uspacyProcessingStateResponseSchema = z.object({
  installation: z
    .object({
      id: z.string().min(1),
      portalDomain: z.string().min(1),
      isActive: z.boolean(),
    })
    .nullable(),
  processing: z
    .object({
      state: uspacyProcessingStateValueSchema,
      subscriptionStatus: uspacyProcessingSubscriptionStatusSchema,
      instructionDocumentId: z.string().nullable(),
      instructionVersion: z.string().nullable(),
      instructionUrl: z.string().nullable(),
      updatedAt: z.string(),
      revision: z.number().int().nonnegative(),
    })
    .nullable(),
  legalDocuments: uspacyLegalDocumentsSchema,
});

/**
 * Why the card tab gets no Aelo session (#746 PR 2, spec
 * `2026-10-29-uspacy-card-tab-session.md` §4). One closed set, answered with
 * 200 so the extension draws it in its `Shell` like the placement status:
 *
 * - `setup_required` — no active installation for the portal;
 * - `portal_ambiguous` — active in two organizations, no basis to choose;
 * - `unavailable` — the organization is outside the pilot or the tab flag is off;
 * - `multiple_portals` — the organization has (or had) another Uspacy portal:
 *   the employee id would mean two people (one installation per organization);
 * - `administrator_action_required` — processing never confirmed, or confirmed
 *   on an instruction older than the embedding purpose;
 * - `withdrawn` — processing withdrawn;
 * - `not_linked` — this employee is not linked to a member of the organization.
 */
export const uspacyEmbeddedRefusalStateSchema = z.enum([
  'setup_required',
  'portal_ambiguous',
  'unavailable',
  'multiple_portals',
  'administrator_action_required',
  'withdrawn',
  'not_linked',
]);

/**
 * The card the tab is open on, as the host route names it
 * (`/crm/{code}/{id}`): code plural (`deals`, `leads`), id numeric. Anything
 * else opens the tab without an entity rather than failing.
 */
export const uspacyEmbeddedSessionRequestSchema = z
  .object({
    entity: z
      .object({ code: z.string().max(32), id: z.string().max(32) })
      .strict()
      .optional(),
  })
  .strict();

/** The installation's condition the frame's status line names. */
const uspacyEmbeddedReadySchema = z.object({
  status: z.literal('ready'),
  /** Processing is paused: stored analyses only, no new calls. */
  paused: z.boolean(),
  /** The installation's credential needs renewing; reading Aelo data is unaffected. */
  reauthRequired: z.boolean(),
});

const uspacyEmbeddedRefusedSchema = z.object({
  status: z.literal('refused'),
  state: uspacyEmbeddedRefusalStateSchema,
});

export const uspacyEmbeddedSessionResponseSchema = z.discriminatedUnion('status', [
  uspacyEmbeddedReadySchema.extend({
    /** The frame address: ui-worker `/embedded/*`, `#bootstrap=` in the fragment. */
    url: z.string().url(),
  }),
  uspacyEmbeddedRefusedSchema,
]);

export const uspacyEmbeddedConfirmResponseSchema = z.discriminatedUnion('status', [
  uspacyEmbeddedReadySchema,
  uspacyEmbeddedRefusedSchema,
  /** The tab's session already ended — open a fresh one, not an error. */
  z.object({ status: z.literal('expired') }),
]);

/** Ticket a portal employee takes to Aelo to link themselves (spec §6). */
export const uspacyEmployeeLinkTicketResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('issued'),
    url: z.string().url(),
    expiresAt: z.string(),
  }),
  uspacyEmbeddedRefusedSchema,
]);

export const uspacyEmployeeLinkRequestSchema = z
  .object({ ticket: z.string().min(32).max(256) })
  .strict();

/** What the signed-in user is about to link — read from the ticket, never the address. */
export const uspacyEmployeeLinkPreviewSchema = z.object({
  portalDomain: z.string(),
  employeeName: z.string().nullable(),
  organizationName: z.string(),
});

export const uspacyEmployeeLinkResultSchema = z.object({
  linked: z.literal(true),
  portalDomain: z.string(),
});

export type UspacyEmbeddedRefusalState = z.infer<typeof uspacyEmbeddedRefusalStateSchema>;
export type UspacyEmbeddedSessionRequest = z.infer<
  typeof uspacyEmbeddedSessionRequestSchema
>;
export type UspacyEmbeddedSessionResponse = z.infer<
  typeof uspacyEmbeddedSessionResponseSchema
>;
export type UspacyEmbeddedConfirmResponse = z.infer<
  typeof uspacyEmbeddedConfirmResponseSchema
>;
export type UspacyEmployeeLinkTicketResponse = z.infer<
  typeof uspacyEmployeeLinkTicketResponseSchema
>;
export type UspacyEmployeeLinkRequest = z.infer<typeof uspacyEmployeeLinkRequestSchema>;
export type UspacyEmployeeLinkPreview = z.infer<typeof uspacyEmployeeLinkPreviewSchema>;
export type UspacyEmployeeLinkResult = z.infer<typeof uspacyEmployeeLinkResultSchema>;

/**
 * The `aelo:*` messages between the Aelo frame and the CRM page that mounts it
 * (#746, spec `2026-10-29-uspacy-card-tab-session.md` §10.1). Types and the
 * height bound only: the extension bundle carries no runtime of this package
 * (`apps/uspacy-extension/test/build.test.ts`), so each side reads a message by
 * hand — and both name these types, which is what keeps the sender of one app
 * and the reader of the other on one shape.
 */
export type EmbeddedHostEntityType = 'deal' | 'lead';

/** The tallest the frame may say it is (`aelo:height`), in CSS pixels. */
export const EMBEDDED_FRAME_MAX_HEIGHT_PX = 20_000;

/** What the frame tells the page. */
export type EmbeddedFrameToHostMessage =
  | { type: 'aelo:height'; height: number }
  | { type: 'aelo:session-expired' }
  | { type: 'aelo:ready' }
  | { type: 'aelo:open-entity'; entityType: EmbeddedHostEntityType; entityId: string };

/** What the page tells the frame. */
export type EmbeddedHostToFrameMessage = {
  type: 'aelo:navigate';
  entityType: EmbeddedHostEntityType;
  entityId: string;
};

/**
 * The tab's entity from the host's route code (#746: deal and lead only, owner
 * 28.10). The plural codes are the route's (`integration-contract.md` §6) and
 * the same the call events carry (`entity_table`).
 */
const USPACY_CARD_TAB_ENTITY_BY_CODE = {
  deals: 'deal',
  leads: 'lead',
} as const;

export function uspacyCardTabEntity(
  entity: { code: string; id: string } | undefined
): { entityType: 'deal' | 'lead'; entityId: string } | null {
  if (!entity) return null;
  // Own keys only: `toString`, `constructor` and the like must not resolve.
  const entityType = Object.prototype.hasOwnProperty.call(
    USPACY_CARD_TAB_ENTITY_BY_CODE,
    entity.code
  )
    ? USPACY_CARD_TAB_ENTITY_BY_CODE[
        entity.code as keyof typeof USPACY_CARD_TAB_ENTITY_BY_CODE
      ]
    : null;
  const entityId = parseCrmTabEntityId(entity.id);
  return entityType && entityId ? { entityType, entityId } : null;
}

export type UspacyPlacementOnboardRequest = z.infer<
  typeof uspacyPlacementOnboardRequestSchema
>;
export type UspacyLegalActivation = z.infer<typeof uspacyLegalActivationSchema>;
export type UspacyLegalDocumentLink = z.infer<typeof uspacyLegalDocumentLinkSchema>;
export type UspacyLegalDocuments = z.infer<typeof uspacyLegalDocumentsSchema>;
export type UspacyProcessingCommand = z.infer<typeof uspacyProcessingCommandSchema>;
export type UspacyProcessingCommandResult = z.infer<
  typeof uspacyProcessingCommandResultSchema
>;
export type UspacyProcessingStateResponse = z.infer<
  typeof uspacyProcessingStateResponseSchema
>;

/**
 * Is an accepted Uspacy document version at least `minimum`? Versions compare
 * by their numeric parts, never as strings: `1.10` is newer than `1.9`. No
 * accepted version, or one with a part that is not wholly digits (`1.10draft`
 * is not `1.10`), is "not enough".
 */
export function uspacyDocumentVersionAtLeast(
  version: string | null | undefined,
  minimum: string
): boolean {
  if (!version) return false;
  const parse = (v: string) =>
    v.split('.').map((part) => (/^\d+$/.test(part) ? Number(part) : Number.NaN));
  const [a, b] = [parse(version), parse(minimum)];
  if (a.some(Number.isNaN)) return false;
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const [x, y] = [a[i] ?? 0, b[i] ?? 0];
    if (x !== y) return x > y;
  }
  return true;
}

/**
 * Processing first named by a version of the Uspacy processing instruction,
 * and that version. The instruction itself says "Material changes to the scope
 * require a new version and fresh authorized confirmation", so an installation
 * that accepted an earlier version does not get this processing — for ITS
 * calls; calls from other sources of the same organization are not its to
 * close — until the Customer accepts a version that names it (#1608).
 *
 * Raising the instruction version for new processing = a new line here and a
 * check of `uspacyInstructionCovers` where that processing starts.
 */
export const USPACY_INSTRUCTION_PURPOSES = {
  /** Aelo's built-in criteria (#868). */
  builtinCriteria: '1.4',
  /** Conversation themes: per-call assignment and the requests over history (#845). */
  conversationThemes: '1.10',
  /** The draft message to the customer from the deal state, on a user's request (#1675). */
  dealClientMessage: '1.15',
  /** The CRM task for a deal's next action, written on a user's request (#2212). */
  dealTask: '1.16',
  /**
   * The deal's or lead's responsible employee (`owner`), read into the deal
   * state's CRM snapshot and set as the responsible of the deal task (#2454).
   * On 1.16 the task is added without one, as that version says.
   */
  dealTaskResponsible: '1.17',
  /**
   * Aelo inside the portal's deal and lead cards for an employee linked to an
   * Aelo user (#746). 1.17 names "create or link the person's Aelo account"
   * (owner decision 24.10: no new document version for the embedding).
   */
  embeddedAccess: '1.17',
  /**
   * The deal's funnel and stage identifiers kept with the times Aelo first and
   * last saw the deal there, for the progression verdict of a CRM without stage
   * history (#2858). Before 1.19 the stage was read once, after the window.
   */
  dealStageHistory: '1.19',
  /**
   * The Pro trial of a free organization (#2816): the processing named for
   * "Pro plan and above" applied to a free organization while its trial runs.
   * Earlier versions name it for the Pro plan and above only, so a trial does
   * not start while an installation in force is on one of them.
   */
  proTrial: '1.20',
} as const;

export type UspacyInstructionPurpose = keyof typeof USPACY_INSTRUCTION_PURPOSES;

/**
 * Does an installation's recorded instruction cover `purpose`? Only an
 * instruction in force does — `active`, or `paused` (intake stopped, stored
 * calls still processed) — and only at a version that names the purpose. No
 * record, `pending` (never confirmed) and `withdrawn` cover nothing new.
 */
export function uspacyInstructionCovers(
  state: { state: string; instructionVersion: string | null } | null | undefined,
  purpose: UspacyInstructionPurpose
): boolean {
  if (!state || (state.state !== 'active' && state.state !== 'paused')) return false;
  return uspacyDocumentVersionAtLeast(
    state.instructionVersion,
    USPACY_INSTRUCTION_PURPOSES[purpose]
  );
}

/** Build canonical public links without duplicating document versions in routes. */
export function buildUspacyLegalDocuments(
  siteOrigin: string,
  requestedLocale: string | null | undefined
): UspacyLegalDocuments {
  const requestedLanguage = normalizeUspacyPlacementLocale(requestedLocale);
  // The localized routes currently provide translated headings and an explicit
  // notice around one governing English body. Record the language the person
  // actually read as English; `requestedLanguage` preserves the shell locale.
  // W13 replaces this fallback only after professional legal translation.
  const routeLanguage = requestedLanguage === 'de' ? 'en' : requestedLanguage;
  const base = siteOrigin.replace(/\/$/, '');
  const link = (
    document: (typeof USPACY_LEGAL_DOCUMENTS)[keyof typeof USPACY_LEGAL_DOCUMENTS]
  ) => ({
    documentId: document.documentId,
    version: document.version,
    effectiveDate: document.effectiveDate,
    // The VERSIONED address: the text this version had, after the next one
    // ships. The document address serves only the version in force, so a
    // recorded acceptance pointing at it would open someone else's text (#1608).
    url: `${base}/${routeLanguage}/legal/${document.path}/${document.version}/`,
    language: 'en' as const,
    requestedLanguage,
    governingLanguage: 'en' as const,
    isLanguageFallback: requestedLanguage !== 'en',
  });

  return {
    terms: link(USPACY_LEGAL_DOCUMENTS.terms),
    privacy: link(USPACY_LEGAL_DOCUMENTS.privacy),
    processingInstruction: link(USPACY_LEGAL_DOCUMENTS.processingInstruction),
  };
}

/**
 * The document address of a recorded link — the version in force, whatever it
 * is. For navigation only (the portal shell's footer links): it never 404s
 * while the site catches up with a new version. Evidence keeps `url`.
 */
export function uspacyLegalDocumentAddress(link: {
  url: string;
  version: string;
}): string {
  const versioned = `/${link.version}/`;
  return link.url.endsWith(versioned)
    ? link.url.slice(0, -versioned.length + 1)
    : link.url;
}

/** Default public site the legal URLs are built against. */
const DEFAULT_SITE_ORIGIN = 'https://aelo.cloud';

/**
 * The site origin legal URLs are recorded against, trailing slash removed.
 *
 * Recorded evidence names a URL, so a trailing slash or a missing env var must
 * not produce two spellings of the same document across call sites — this was
 * written out five times with its own `?? 'https://aelo.cloud'` each.
 */
export function uspacySiteOrigin(env: { PUBLIC_SITE_ORIGIN?: string }): string {
  return env.PUBLIC_SITE_ORIGIN?.replace(/\/$/, '') ?? DEFAULT_SITE_ORIGIN;
}

/**
 * The append-only acceptance rows recorded when an Aelo-side representative
 * authorizes Uspacy processing.
 *
 * Terms are ACCEPTED and the privacy notice is ACKNOWLEDGED — two legally
 * distinct actions whose document/version/url must match the registry the
 * customer was shown. Written out per call site (three of them), the array was
 * one copy-paste away from recording an action against the wrong document.
 * The processing INSTRUCTION is deliberately not here: it is the parent record
 * these acceptances hang from, not a third row.
 */
export function buildUspacyLegalAcceptances(params: {
  legalDocuments: UspacyLegalDocuments;
  /** The Uspacy-side actor, when the evidence comes from the placement. */
  externalActorId?: string | null;
  authorityBasis: string;
  source: string;
}): Array<{
  externalActorId: string | null;
  integrationVendor: 'uspacy';
  documentId: string;
  documentVersion: string;
  language: string;
  canonicalUrl: string;
  action: 'accepted' | 'acknowledged';
  authorityBasis: string;
  source: string;
  metadataJson: string;
}> {
  const { legalDocuments, externalActorId = null, authorityBasis, source } = params;
  const row = (
    document: UspacyLegalDocumentLink,
    action: 'accepted' | 'acknowledged'
  ) => ({
    externalActorId,
    integrationVendor: 'uspacy' as const,
    documentId: document.documentId,
    documentVersion: document.version,
    language: document.language,
    canonicalUrl: document.url,
    action,
    authorityBasis,
    source,
    metadataJson: '{}',
  });

  return [
    row(legalDocuments.terms, 'accepted'),
    row(legalDocuments.privacy, 'acknowledged'),
  ];
}

const numericIdSchema = z.union([
  z.number().int().nonnegative(),
  z.string().regex(/^\d+$/, 'ID must be numeric'),
]);

export const uspacyEntityTableSchema = z.enum([
  'leads',
  'deals',
  'contacts',
  'companies',
]);

export const uspacyCallStatusSchema = z.enum([
  'success',
  'missed',
  'declined',
  'subscriber_unavailable',
  'number_not_exist',
  'call_failure',
]);

/**
 * Public Uspacy call-event representation.
 *
 * `.passthrough()` is deliberate: the connector writes back only the documented
 * allowlist, while preserving compatibility with response fields Uspacy adds.
 *
 * `entity_table` is a bare string rather than the four-way enum the PATCH body
 * documents. The LIST response types it as an open string, and a portal with a
 * custom CRM entity would otherwise fail the whole parse — losing a call over a
 * field only used for optional entity binding. Consumers narrow it themselves
 * (`uspacyEntityTableSchema.safeParse`).
 */
export const uspacyCallEventSchema = z
  .object({
    id: numericIdSchema,
    integration_code: z.string().min(1),
    external_id: numericIdSchema.nullish(),
    tmp_id: numericIdSchema.nullish(),
    task_id: numericIdSchema.nullish(),
    contact_id: numericIdSchema.nullish(),
    company_id: numericIdSchema.nullish(),
    lead_id: numericIdSchema.nullish(),
    deal_id: numericIdSchema.nullish(),
    entity_table: z.string().nullish(),
    employees: z.array(z.unknown()).nullish(),
    contacts: z.array(z.unknown()).nullish(),
    companies: z.array(z.unknown()).nullish(),
    subject: z.string().nullish(),
    call_type: z.enum(['outgoing', 'incoming']),
    ended_call_status: uspacyCallStatusSchema.nullish(),
    from: z.string().min(1),
    to: z.string().nullish(),
    begin_time: z.number().int().nonnegative().nullish(),
    end_time: z.number().int().nonnegative().nullish(),
    duration: z.number().int().nonnegative().nullish(),
    call_record_link: z.string().url().nullish(),
    note: z.string().nullish(),
  })
  .passthrough();

/**
 * `GET /crm/v1/events/call` — a bare array of call events, no pagination
 * envelope. One malformed row must not discard the page, so rows are filtered
 * rather than parsed as a whole.
 */
export const uspacyCallEventListSchema = z.array(z.unknown());

/**
 * `GET /company/v1/users/me`.
 *
 * `email` is typed by the reference as an ARRAY of `{value, main}` objects, but
 * a live portal was observed returning a plain string for the same field. Both
 * are accepted: a placement that cannot read the operator's address cannot
 * onboard them at all, and guessing which of the two is "correct" would break
 * whichever portal disagrees.
 */
export const uspacyUserProfileSchema = z
  .object({
    id: numericIdSchema,
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    email: z.union([
      z.string(),
      z.array(
        z
          .object({ value: z.string().nullish(), main: z.boolean().nullish() })
          .passthrough()
      ),
    ]),
  })
  .passthrough();

/**
 * `GET /company/v1/users/me/settings` — only `lang` matters here. Documented
 * values are `en-US | pt-BR | pl | es-ES | uk`; note the absence of Russian,
 * which Aelo does support.
 */
export const uspacyUserSettingsSchema = z
  .object({ lang: z.string().nullish() })
  .passthrough();

/**
 * Normalized queue message produced by the thin Uspacy event receiver.
 * Raw partner payloads are adapted to this shape at the HTTP boundary.
 *
 * Exactly one of the two ids identifies the work:
 *
 *  - `callEventId` — the payload named the call event outright;
 *  - `activityId`  — the payload named the CRM activity («справа»), which is
 *    what the subscription we create actually delivers. The call event is then
 *    resolved by its `task_id` during enrichment.
 */
export const uspacyWebhookIntakeSchema = z
  .object({
    source: z.literal('uspacy'),
    portalDomain: z.string().min(1).max(253),
    event: z.string().min(1).max(128),
    callEventId: z.string().regex(/^\d+$/, 'callEventId must be numeric').optional(),
    activityId: z.string().regex(/^\d+$/, 'activityId must be numeric').optional(),
    idempotencyKey: z.string().min(1).max(512),
    receivedAt: z.string().datetime().optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.callEventId && !value.activityId) {
      ctx.addIssue({
        code: 'custom',
        path: ['callEventId'],
        message: 'Either callEventId or activityId is required',
      });
    }
  });

/**
 * Adapter contract for an inbound Uspacy event.
 *
 * The delivered shape is not pinned by the partner, so this accepts the keys a
 * call event could plausibly arrive under AND the CRM-activity payload the
 * subscription is expressed in (`{service:'crm', type:'task'}`), where `id` is
 * the ACTIVITY id and `type` states what kind of activity it was. Tenant
 * routing never comes from here — it comes from the verified caller.
 */
export const uspacyCallWebhookSchema = z
  .object({
    event: z.string().min(1).max(128),
    data: z
      .object({
        call_event_id: numericIdSchema.optional(),
        callEventId: numericIdSchema.optional(),
        /** CRM activity id when the payload is an activity event. */
        id: numericIdSchema.optional(),
        /** CRM activity kind: task | call | meeting | chat | email. */
        type: z.string().optional(),
      })
      .passthrough(),
  })
  .superRefine((value, ctx) => {
    if (
      value.data.call_event_id === undefined &&
      value.data.callEventId === undefined &&
      value.data.id === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['data'],
        message: 'Call event ID is required',
      });
    }
  });

/**
 * Body sent by Uspacy to the Marketplace install callback.
 *
 * Shape confirmed by a shipped Uspacy Marketplace app: `{ token, refresh_token,
 * expiry_date, installer_uid }`. `expiry_date` is deliberately `unknown` here —
 * the partner has been observed sending epoch seconds, epoch milliseconds and
 * numeric strings, and a schema that rejects any of them turns a working
 * install into a 400. Normalization happens in one place instead
 * (`normalizeUspacyExpiry`).
 */
export const uspacyMarketplaceInstallSchema = z.object({
  token: z.string().min(1),
  refresh_token: z.string().min(1),
  expiry_date: z.unknown().optional(),
  /** Uspacy user id of whoever installed the app. */
  installer_uid: z.union([z.string(), z.number()]).nullish(),
});

/** Canonical refresh response documented by Uspacy. */
export const uspacyRefreshResponseSchema = z.object({
  jwt: z.string().min(1),
  refreshToken: z.string().min(1),
  expireInSeconds: z.coerce.number().int().positive(),
});

export type UspacyEntityTable = z.infer<typeof uspacyEntityTableSchema>;
export type UspacyUserProfile = z.infer<typeof uspacyUserProfileSchema>;
export type UspacyUserSettings = z.infer<typeof uspacyUserSettingsSchema>;
export type UspacyCallStatus = z.infer<typeof uspacyCallStatusSchema>;
export type UspacyCallEvent = z.infer<typeof uspacyCallEventSchema>;
export type UspacyWebhookIntakeMessage = z.infer<typeof uspacyWebhookIntakeSchema>;
export type UspacyCallWebhook = z.infer<typeof uspacyCallWebhookSchema>;
export type UspacyMarketplaceInstall = z.infer<typeof uspacyMarketplaceInstallSchema>;
export type UspacyRefreshResponse = z.infer<typeof uspacyRefreshResponseSchema>;
